/*
 * @Author: Ryan
 * @Date: 2026-01-21
 * @Description: UGC Generator 主组件
 */
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';

import { ExtendedUploadFile, FILE_STATUS } from '@/components/FileUpload';

import { generatorOptions } from './config';
import styles from './index.module.scss';
import PromptInput from './PromptInput';
import QuickCards from './QuickCards';
import { useUGCNavigationStore } from './store';
import SubmitButton from './SubmitButton';
import TypeSelector from './TypeSelector';
import { writeToTargetStore } from './utils/writeToTargetStore';

const ResourceUpload = dynamic(() => import('./ResourceUpload'), {
  ssr: false,
});

interface UGCGeneratorProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
}

const UGCGenerator: React.FC<UGCGeneratorProps> = ({ title, subtitle }) => {
  const router = useRouter();
  const [prompt, setPrompt] = useState('');
  const [selectedType, setSelectedType] = useState<string>('ugc-video-ads');
  const [fileList, setFileList] = useState<ExtendedUploadFile[]>([]);

  const currentOption = useMemo(() => {
    return (
      generatorOptions.find((opt) => opt.key === selectedType) || generatorOptions[0]
    );
  }, [selectedType]);

  const hasUploadedImage = useMemo(() => {
    return fileList.some((f) => f.status === FILE_STATUS.SUCCESS);
  }, [fileList]);

  const canSubmit = useMemo(() => {
    const isUploading = fileList.some((f) => f.status === FILE_STATUS.UPLOADING);
    if (isUploading) return false;
    return prompt.trim().length > 0 || hasUploadedImage;
  }, [prompt, fileList, hasUploadedImage]);

  const handleSubmit = () => {
    if (!canSubmit) return;

    writeToTargetStore(selectedType, prompt, fileList, hasUploadedImage);

    const targetTabKey = hasUploadedImage
      ? currentOption.imageTabKey
      : currentOption.textTabKey;

    if (targetTabKey) {
      useUGCNavigationStore.getState().setTargetTabKey(targetTabKey);
    }

    router.push(currentOption.route);
  };

  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    setFileList([]);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* 标题区域 */}
        <div className={styles.header}>
          <h1 className={styles.title}>{title || 'AI UGC Video Generator'}</h1>
          <p className={styles.subtitle}>
            {subtitle || (
              <>
                Empower your brand with FalcoCut to generate high-quality videos in
                minutes.
                <br />
                Turn text or images into engaging UGC-style videos without complex
                filming, expensive production, or professional equipment.
              </>
            )}
          </p>
        </div>

        {/* 输入区域 */}
        <div className={styles.inputArea}>
          <div className={styles.inputCard}>
            <PromptInput
              value={prompt}
              onChange={setPrompt}
              onSubmit={handleSubmit}
              placeholder="Enter your product details, category, key features, promo info, or selling point"
            />

            <div className={styles.toolbar}>
              <div className={styles.toolbarLeft}>
                <ResourceUpload
                  show={true}
                  fileList={fileList}
                  onChange={setFileList}
                  selectedType={selectedType}
                />
                <TypeSelector
                  value={selectedType}
                  onChange={handleTypeChange}
                  options={generatorOptions}
                />
              </div>
              <SubmitButton onClick={handleSubmit} disabled={!canSubmit} />
            </div>
          </div>
        </div>

        {/* 展示卡片 - 点击切换下拉选项 */}
        <QuickCards onSelectType={handleTypeChange} />
      </div>
    </div>
  );
};

export default UGCGenerator;
